import React, { useState, useEffect } from "react";

import { useAuth0 } from "react-auth0-wrapper";

import { Grid, Typography } from "@mui/material";

import { ReadApps } from "views/MyApps/ReadApps";
import { CreateApp } from "views/MyApps/CreateApp";
import { NeedEmailVerification } from "views/MyApps/NeedEmailVerification";
import { Guidance } from "views/MyApps/Guidance";

const BASE_URL = "https://dev-portal-server-evcjgpbjasa5c5a7.southcentralus-01.azurewebsites.net";

export function MyApps() {
  const { loading, user, getIdTokenClaims } = useAuth0();

  async function authFetch(url, options = {}) {
    const claims = await getIdTokenClaims();
    const token = claims.__raw;
    return fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        Authorization: `Bearer ${token}`,
      },
    });
  }

  const defaultMessageState = {
    isMessage: false,
    messageType: "",
    messageText: "",
  };

  const [crudState, setCrudState] = useState("read");
  const [appsLoaded, setAppsLoaded] = useState(false);
  const [apps, setApps] = useState([]);
  const [messageState, setMessageState] = useState(defaultMessageState);
  const [resentEmail, setResentEmail] = useState("");
  const [registerButtonDisabled, setRegisterButtonDisabled] = useState(false);

  // This works for now, but will need to be changed if we allow application
  // data to be *edited*, as comparing the length of the app array will not
  // detect a change to an app attribute.
  function appArraysAreDifferent(a1, a2) {
    if (a1.length !== a2.length) return true;
  }

  function fetchApps() {
    if (messageState.isMessage && messageState.messageType !== "success") {
      return;
    }
    authFetch(`${BASE_URL}/users/${user.email}/apps`)
      .then((response) => response.json())
      .then((data) =>
        appArraysAreDifferent(apps, data.apps) ? setApps(data.apps) : null
      )
      .then(() => setAppsLoaded(true))
      .catch((error) => {
        const failureMessage = {
          isMessage: true,
          messageType: "warning",
          messageText: `Your apps could not be retrieved now. (${error})`,
        };
        setMessageState(failureMessage);
      });
  }

  function registerApp(user, appName, applicationType) {
    const applicationTypeSlug = applicationType
      ? "3rd-party-dev"
      : "customer-dev";
    const successMessage = {
      isMessage: true,
      messageType: "success",
      messageText: `Your app, ${appName}, was registered successfully.`,
    };
    authFetch(`${BASE_URL}/users/${user.email}/apps`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name: appName,
        applicationType: applicationTypeSlug,
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
      })
      .then(() => setMessageState(successMessage))
      .catch((error) => {
        const failureMessage = {
          isMessage: true,
          messageType: "warning",
          messageText: `Your app, ${appName}, could not be registered now. (${error})`,
        };
        setMessageState(failureMessage);
      })
      .finally(() => {
        setCrudState("read");
        setRegisterButtonDisabled(false);
      });
  }

  function deleteApp(appId, appName) {
    const successMessage = {
      isMessage: true,
      messageType: "success",
      messageText: `Your app, ${appName}, was deleted successfully.`,
    };
    authFetch(`${BASE_URL}/users/${user.email}/apps/${appId}`, { method: "DELETE" })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
      })
      .then(() => setMessageState(successMessage))
      .catch((error) => {
        const failureMessage = {
          isMessage: true,
          messageType: "warning",
          messageText: `Your app, ${appName}, could not be deleted now. (${error})`,
        };
        setMessageState(failureMessage);
      })
      .finally(() => {
        authFetch(`${BASE_URL}/users/${user.email}/apps`)
          .then((response) => response.json())
          .then((data) =>
            appArraysAreDifferent(apps, data.apps) ? setApps(data.apps) : null
          )
          .then(() => window.scrollTo(0, 0));
      });
  }

  function resendVerificationEmail() {
    setResentEmail("sending...");
    authFetch(`${BASE_URL}/users/${user.sub}/ResendVerificationEmail`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
      })
      .then(() => setResentEmail("sent"));
  }

  useEffect(() => {
    if (user) {
      fetchApps();
    }
  });

  if (loading || !user) {
    return (
      <Grid container>
        <Grid item md={1} />
        <Grid item md={10}>
          <Typography variant="body2">Loading...</Typography>
        </Grid>
      </Grid>
    );
  }

  if (!user.email_verified) {
    return (
      <NeedEmailVerification
        resend={resendVerificationEmail}
        resentEmail={resentEmail}
      />
    );
  }

  if (crudState === "read") {
    return (
      <Grid container>
        <Grid item md={1} />
        <Grid item md={10}>
          <h1>
            Apps registered to
            {" " + user.email}
          </h1>
          <Guidance appsLoaded={appsLoaded} apps={apps} />
        </Grid>
        <Grid item md={1} />
        <Grid item md={1} />
        <Grid item md={10}>
          <ReadApps
            apps={apps}
            setCrudState={setCrudState}
            messageState={messageState}
            handleDeletion={deleteApp}
          />
        </Grid>
        <Grid item md={1} />
      </Grid>
    );
  }

  if (crudState === "create") {
    return (
      <CreateApp
        user={user}
        setCrudState={setCrudState}
        registerApp={registerApp}
        registerButtonDisabled={registerButtonDisabled}
        setRegisterButtonDisabled={setRegisterButtonDisabled}
      />
    );
  }
}
